import { createContext, ReactNode, useEffect, useState } from 'react';

import { IAuth } from 'store/types/authTypes';
import { IConvertSize } from 'shared/types/ConvertSize';
import { Loader } from 'shared/components/Loader';
import { requestService } from 'shared/utils/services/requestService';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { t } from 'localization/utils/t';
import { startCase } from 'shared/utils/startCase';
import { useGenderLabel } from 'shared/hooks/useGenderLabel';

const TP = 'productTemplate.providers.SizeConversionProvider';

interface ISizeConversionProvider {
  children: ReactNode;
}

export const SizeConversionContext = createContext(null);

export const SizeConversionProvider = ({ children }: ISizeConversionProvider) => {
  const getGenderLabel = useGenderLabel();
  const { isFashionProduct, productType, singleGender, sizeBrand, sizeUnit } = useStoreSelector(
    (state) => state.pdp.productTemplate,
  );
  const isApparel = isFashionProduct || (productType && productType !== 'sneakers');
  const { user }: IAuth = useStoreSelector((state) => state.auth);
  const {
    convertedSize: userSize,
    sizeBrand: userSizeBrand,
    sizeGender: userSizeGender,
    convertedSizeUnit: userSizeUnit,
  } = user || {};

  const [convertsToTitle, setConvertsToTitle] = useState<string>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [convertedUserSize, setConvertedUserSize] = useState<IConvertSize>(null);

  useEffect(() => {
    const getConvertedSize = async () => {
      try {
        setIsLoading(true);
        const { data: convertedSize }: { data: IConvertSize } = await requestService({
          url: '/web-api/v1/convert_size',
          method: 'post',
          data: JSON.stringify({
            size: String(userSize),
            from_brand: userSizeBrand || 'default',
            to_brand: sizeBrand,
            from_gender: userSizeGender?.toUpperCase(),
            to_gender: singleGender?.toUpperCase(),
            from_unit: userSizeUnit?.toUpperCase(),
            to_unit: sizeUnit?.toUpperCase(),
            product_type: 'SNEAKERS',
          }),
        });

        setConvertedUserSize(convertedSize);

        if (
          convertedSize?.unit?.toLowerCase() !== userSizeUnit?.toLowerCase() ||
          convertedSize?.gender?.toLowerCase() !== userSizeGender?.toLowerCase() ||
          convertedSize?.size !== String(userSize)
        ) {
          const userTitle = t(`${TP}.userTitle`, '{sizeUnit} {gender} Size {size}', {
            sizeUnit: userSizeUnit.toUpperCase(),
            gender: startCase(getGenderLabel(userSizeGender), {
              capitalizeInOtherLanguages: false,
            }),
            size: String(userSize),
          });
          const convertedTitle = t(`${TP}.convertedTitle`, '{sizeUnit} {gender} Size {size}', {
            sizeUnit: convertedSize?.unit?.toUpperCase(),
            gender: startCase(getGenderLabel(convertedSize?.gender), {
              capitalizeInOtherLanguages: false,
            }),
            size: convertedSize?.size,
          });
          setConvertsToTitle(
            t(`${TP}.setConvertesToTitle`, '{userTitle} converts to {convertedTitle}', {
              userTitle,
              convertedTitle,
            }),
          );
        } else {
          setConvertedUserSize(null);
          setConvertsToTitle(null);
        }
      } catch {
        setConvertedUserSize(null);
        setConvertsToTitle(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (!isApparel && userSizeGender && userSizeUnit && singleGender && sizeBrand && sizeUnit) {
      void getConvertedSize();
    }

    return () => {
      setIsLoading(false);
    };
  }, [isApparel, userSize, userSizeBrand, userSizeGender, userSizeUnit]);

  const convertsToSizeDisplay = isLoading ? <Loader /> : convertsToTitle;

  const values = { convertedUserSize, convertsToSizeDisplay, isLoading };

  return <SizeConversionContext.Provider value={values}>{children}</SizeConversionContext.Provider>;
};
